import type { TypeIconClasses } from '.';
import type { ReadChangelogType } from '@readme/api/src/mappings/changelog/types';
import type { HubResponseProps } from '@readme/iso';
import type { Opts } from '@readme/iso/src/types/rdmd';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';

import useClassy from '@core/hooks/useClassy';
import useRdmdOpts from '@core/hooks/useRdmdOpts';
import {
  InitializeSuperHubDocument,
  ConnectSuperHubDocumentToApi,
  useProjectStore,
  useSuperHubStore,
} from '@core/store';

import Button from '@ui/Button';
import DateLine, { LocalizedDateLine } from '@ui/DateLine';
import Flex from '@ui/Flex';
import RDMD from '@ui/RDMD';
import Spinner from '@ui/Spinner';

import classes from './style.module.scss';

interface LocationState {
  fromChangelogList: boolean;
}

function Back({ children, className, ...props }) {
  const bem = useClassy(classes, 'ChangelogPost');
  const { state } = useLocation<LocationState | undefined>();
  const history = useHistory();

  return state?.fromChangelogList ? (
    <Button {...props} className={bem(className, '_back')} onClick={() => history.goBack()}>
      {children}
    </Button>
  ) : (
    <Link {...props} className={bem(className, '_back')} to="/changelog">
      {children}
    </Link>
  );
}

interface ChangelogPostProps {
  changelog?: ReadChangelogType['data'];
  className?: string;
  initialChangelog?: ReadChangelogType['data'];
  isList?: boolean;
  rdmd?: HubResponseProps['rdmd'];
  rdmdOpts?: Opts;
  typeIconClasses: TypeIconClasses;
}

function Content({
  changelog: changelogProp,
  className,
  initialChangelog,
  isList = false,
  typeIconClasses,
  rdmd,
}: ChangelogPostProps) {
  const bem = useClassy(classes, 'ChangelogPage');
  const { t } = useTranslation();
  const [changelog, isLoading, isi18nEnabled, language] = useSuperHubStore(s => [
    changelogProp ?? s.document.getChangelogData(),
    s.document.isLoading && (s.document.data ? s.document.data !== initialChangelog : true),
    s.isI18nEnabled,
    s.language,
  ]);
  const [showAuthor, showExactDate] = useProjectStore(s => [
    !!s.data.appearance.changelog.show_author,
    !!s.data.appearance.changelog.show_exact_date,
  ]);

  const rdmdOpts = useRdmdOpts();

  const { type = 'none', title, author, created_at: createdAt = '', slug } = changelog || {};
  const changelogIcon = showExactDate ? '' : 'icon-clock';
  const changelogFormat = showExactDate ? { fnsFormat: 'PPP' } : {};

  return (
    <article
      className={bem(className, '&', '_list', 'rm-Changelog-post')}
      id={title?.replace(/\s+/g, '-').toLowerCase()}
    >
      {!isList ? (
        <Flex align="center" className={bem('_back')} justify="end">
          <Back className="Button Button_sm Button_text" size="sm">
            <span className={bem('icon-arrow-left2', 'ChangelogPost_back-icon')} />
            Back to All
          </Back>
        </Flex>
      ) : (
        <Flex align="end" className={bem('_list-date')} dateTime={createdAt} gap="xs" layout="col" tag="time">
          {isi18nEnabled ? (
            <LocalizedDateLine
              exactDate={showExactDate}
              icon={showExactDate ? undefined : 'clock'}
              locale={language}
              time={createdAt}
            />
          ) : (
            <DateLine icon={changelogIcon} time={createdAt} {...changelogFormat} />
          )}
          {!!isList && type !== 'none' && (
            <Flex align="center" className={bem('ChangelogPost_type', '_list-type')} gap="0">
              {!!typeIconClasses[type] && (
                <span className={bem(typeIconClasses[type], 'ChangelogIcon', '_list-icon')} />
              )}
              {t(`changelog.${type}`)}
            </Flex>
          )}
        </Flex>
      )}
      {isLoading ? (
        <div className={bem('ChangelogPost', '_list-post')}>
          <Spinner className={bem('ChangelogSpinner')} size="xxl" />
        </div>
      ) : (
        <div className={bem('ChangelogPost', '_list-post')}>
          {type !== 'none' && !isList && (
            <div className={bem('ChangelogPost_type')}>
              {!!typeIconClasses[type] && <span className={bem(typeIconClasses[type], 'ChangelogIcon')} />}
              {t(`changelog.${type}`)}
            </div>
          )}
          <header className={bem('ChangelogPost_header')}>
            {!isList ? (
              <h1 className={bem('ChangelogPost_title')}>{title}</h1>
            ) : (
              <Flex gap="0" layout="col">
                <Link
                  to={{
                    pathname: `/changelog/${slug}`,
                    state: { fromChangelogList: true } as LocationState,
                  }}
                >
                  <h1 className={bem('ChangelogPost_title')}>{title}</h1>
                </Link>
                {!!showAuthor && !!author?.name && (
                  <address className={bem('ChangelogPost-author')}>
                    {t('attribution', { attribution: author.name })}
                  </address>
                )}
              </Flex>
            )}
            {!!createdAt &&
              !isList &&
              (isi18nEnabled ? (
                <LocalizedDateLine
                  attribution={showAuthor && author?.name ? author.name : undefined}
                  className={bem('_date', '_noicon')}
                  exactDate={showExactDate}
                  icon={showExactDate ? undefined : 'clock'}
                  time={createdAt}
                />
              ) : (
                <DateLine
                  className={bem('ChangelogPost_date')}
                  icon={changelogIcon}
                  suffix={showAuthor && author?.name ? `by ${author.name}` : undefined}
                  time={createdAt}
                  {...changelogFormat}
                />
              ))}
          </header>
          <section key={slug} className={bem('ChangelogPost_text')}>
            <RDMD body={changelog?.content.body} dehydrated={rdmd?.dehydrated?.body} format="mdx" opts={rdmdOpts} />
          </section>
        </div>
      )}
    </article>
  );
}

/**
 * Renders changelog posts in "view" mode.
 */
export default function ChangelogPost(props: ChangelogPostProps) {
  const { initialChangelog = null, isList = false } = props;

  return isList ? (
    <Content {...props} />
  ) : (
    <InitializeSuperHubDocument document={initialChangelog}>
      <ConnectSuperHubDocumentToApi>
        <main className="rm-Changelog" id="content">
          <div className="rm-Container rm-ContainerNarrow">
            <Content {...props} />
          </div>
        </main>
      </ConnectSuperHubDocumentToApi>
    </InitializeSuperHubDocument>
  );
}
